import { getHostRuntime } from './host-runtime'
import type { ResolvedPageContext } from './pipeline/page-context'
import type { ConfirmedEntity, PendingToolCall, SessionContext } from './types'

const SESSION_TTL_MS = 30 * 60_000

let cachedContext: SessionContext | null = null

// Per-turn callbacks — set by Conversation before processing a turn, cleared after.
// The session singleton stays clean; only the per-call ctx returned by
// getSessionContext() during a turn carries these.
let _confirmedEntityWriter: ((entity: ConfirmedEntity) => void) | null = null
let _registerToolCallResolver:
  | ((
      toolCallId: string,
      pendingCall: PendingToolCall,
      resolve: (result: unknown) => void,
      reject: (reason: Error) => void,
    ) => void)
  | null = null
let _resolveToolCallResolver:
  | ((toolCallId: string, result: unknown) => void)
  | null = null
let _pageContext: ResolvedPageContext | null = null

export function setConfirmedEntityWriter(
  writer: ((entity: ConfirmedEntity) => void) | null,
): void {
  _confirmedEntityWriter = writer
}

export function setPerTurnCallbacks(
  callbacks: {
    registerToolCallResolver: (
      toolCallId: string,
      pendingCall: PendingToolCall,
      resolve: (result: unknown) => void,
      reject: (reason: Error) => void,
    ) => void
    resolveToolCallResolver: (toolCallId: string, result: unknown) => void
    pageContext: ResolvedPageContext
  } | null,
): void {
  _registerToolCallResolver = callbacks?.registerToolCallResolver ?? null
  _resolveToolCallResolver = callbacks?.resolveToolCallResolver ?? null
  _pageContext = callbacks?.pageContext ?? null
}

/** Clears the session cache. Used in tests to reset between cases. */
export function invalidateSessionContext(): void {
  cachedContext = null
}

export async function getSessionContext(): Promise<SessionContext> {
  const baseValid =
    cachedContext && Date.now() - cachedContext.fetchedAt < SESSION_TTL_MS

  if (!baseValid) {
    const currentUserId = await getHostRuntime().bindings.currentUserId()

    cachedContext = {
      currentUserId,
      schemas: new Map(),
      resolveCache: new Map(),
      featureFlags: new Set(),
      fetchedAt: Date.now(),
    }
  }

  // Refresh per-turn callbacks on every read — Conversation may have
  // installed different ones between turns or cleared them on abort.
  return {
    ...cachedContext!,
    featureFlags: getHostRuntime().bindings.capabilities,
    addConfirmedEntity: _confirmedEntityWriter ?? undefined,
    registerToolCallResolver: _registerToolCallResolver ?? undefined,
    resolveToolCallResolver: _resolveToolCallResolver ?? undefined,
    pageContext: _pageContext ?? undefined,
  }
}
